<template>
  <ClaimsEvNew
    :is-mobile="isMobile"
    :saving="claimSaving || claimFilesSaving"
    :submitted="submitted"
    :leases="evVehicleLeases"
    :claim-types="
      Object.values($Constants.CLAIM_TYPES).filter(
        (type) => type.value === 'fuel/electricity'
      )
    "
    :lease-id="payload.leaseId"
    :amount="updateAmount"
    :claim-type="payload.claimType"
    :account-ending-in="accountEndingIn"
    :files="files"
    :errors="$v"
    :images="images"
    :entity="user.entity"
    :file-requirements="fileRequirements"
    :bank-details-error="bankDetailsError"
    :odometer-from-date="odometerFromDate"
    :odometer-from="odometerFrom"
    :odometer-to="odometerTo"
    :total-kilometers="totalKilometers"
    @select:lease="payload = { ...payload, leaseId: $event }"
    @select:claim-type="payload = { ...payload, claimType: $event }"
    @update:odometerFrom="updateOdometerFrom($event)"
    @update:odometerTo="updateOdometerTo($event)"
    @update:files="updateFiles"
    @open:bank-details="openBankDetails"
    @close="close"
    @restart="restart"
    @submit="submit"
  />
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import validations from './validations';
import { getAsset } from 'ecx.saigon.ui.common';
import PayToDialog from '@/components/organism/pay-to-dialog';
import { parseDate } from '@eui-util/date';

const CREATE_BASE_PAYLOAD = {
  leaseId: undefined,
  amountTotal: undefined,
  claimType: 'fuel/electricity',
  accountName: undefined,
  bsb: undefined,
  accountNumber: undefined,
  description: undefined
};

export default {
  name: 'ClaimsEvNewWidget',
  validations,
  data() {
    return {
      images: {},
      files: [],
      fileRequirements: this.$Constants.CLAIM_FILE_REQUIREMENTS,
      submitted: false,
      payload: { ...CREATE_BASE_PAYLOAD },
      bankDetailsError: '',
      odometerFrom: 0,
      odometerTo: 0
    };
  },
  computed: {
    ...mapState({
      user: (state) => state.user.user,
      hasLeases: (state) => state.lease.hasLeases,
      leases: (state) => state.lease.leases,
      claimSaving: (state) => state.claim.claimSaving,
      claimFilesSaving: (state) => state.claim.claimFilesSaving
    }),
    ...mapGetters('lease', ['evVehicleLeases']),
    isMobile() {
      return this.$q.screen.xs;
    },
    accountEndingIn() {
      if (!this.payload.accountNumber) {
        return;
      }

      return this.payload.accountNumber
        ? this.payload.accountNumber.toString().replace(/\W/g, '').substr(-4)
        : undefined;
    },
    odometerFromDate() {
      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      const prevYear = currentYear - 1;

      return this.evVehicleLeases[0].leaseStartDate < `${prevYear}-04-01`
        ? `01/04/${prevYear}*`
        : `${parseDate(this.evVehicleLeases[0].leaseStartDate, 'dd/MM/yyyy')}*`;
    },
    totalKilometers() {
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      this.payload.description = `Opening reading: ${
        this.odometerFrom
      } Closing reading: ${this.odometerTo} Total kilometres: ${
        this.odometerTo - this.odometerFrom
      }`;
      return this.odometerTo - this.odometerFrom < 0
        ? 0
        : this.odometerTo - this.odometerFrom;
    },
    updateAmount() {
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      this.payload.amountTotal = Number(
        ((this.odometerTo - this.odometerFrom) * 0.042).toFixed(2)
      );
      return this.payload.amountTotal < 0 ? 0 : this.payload.amountTotal;
    }
  },
  methods: {
    ...mapActions('claim', ['createClaim', 'saveClaimFiles']),
    updateFiles(files) {
      this.files = files;
    },
    openBankDetails() {
      const { accountName, bsb, accountNumber } = this.payload;
      this.$q
        .dialog({
          fullScreen: this.isMobile,
          initialData: { accountName, bsb, accountNumber },
          component: PayToDialog
        })
        .onOk(async (payToData) => {
          this.payload = { ...this.payload, ...payToData };
          this.bankDetailsError = '';
        });
    },
    async submit() {
      this.$v.$touch();
      this.bankDetailsError = '';

      if (!this.payload.accountNumber) {
        this.bankDetailsError = 'Bank Details is required.';
        return;
      }
      if (this.$v.$error) {
        return;
      }

      await this.createClaim({
        payload: this.payload,
        files: this.files
      });
      this.submitted = true;
    },
    restart() {
      this.files = [];
      this.payload = { ...CREATE_BASE_PAYLOAD, leaseId: this.leases[0].id };
      this.$v.$reset();
      this.submitted = false;
      this.bankDetailsError = '';
      this.odometerFrom = 0;
      this.odometerTo = 0;
    },
    close() {
      this.$router.push({
        name: this.$RouteNames.claims
      });
    },
    updateOdometerFrom(event) {
      this.odometerFrom = event;
    },
    updateOdometerTo(event) {
      this.odometerTo = event;
    }
  },
  async beforeMount() {
    this.restart();
    this.images = {
      ...this.images,
      claimSubmitted: await getAsset(this.$Constants.CLAIM_SUBMITTED_IMG_PATH)
    };
  },
  mounted() {
    if (!this.hasLeases || !this.leases.length) {
      return this.$router.push({
        name: this.$RouteNames.dashboard
      });
    }

    if (this.leases.length === 1) {
      this.payload = { ...this.payload, leaseId: this.leases[0].id };
    }
  },
  components: {
    ClaimsEvNew: () => import('../../components/page/claims-ev-new')
  }
};
</script>
